import * as React from 'react'
import styled from 'styled-components'

const Wrapper = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(10px, 3vw, 16px);
  overflow: auto;
  overscroll-behavior: contain;
  pointer-events: none;

  @media (max-height: 420px) {
    align-items: flex-start;
  }
`

const Panel = styled.div`
  width: min(380px, calc(100vw - 32px));
  max-height: calc(100dvh - 32px);
  padding: clamp(18px, 5vw, 24px) clamp(18px, 5vw, 24px) clamp(16px, 5vw, 22px);
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 8px;
  background: linear-gradient(180deg, rgba(8, 15, 28, 0.84) 0%, rgba(15, 23, 42, 0.72) 100%);
  box-shadow: 0 26px 80px rgba(2, 6, 23, 0.34);
  backdrop-filter: blur(18px);
  overflow: auto;
  pointer-events: auto;
`

const Eyebrow = styled.div`
  margin-bottom: 10px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(148, 163, 184, 0.82);
`

const Title = styled.div`
  font-size: 1.4rem;
  font-weight: 750;
  line-height: 1.16;
  letter-spacing: 0;
  color: #f8fafc;
`

const Description = styled.div`
  margin-top: 10px;
  font-size: 0.95rem;
  line-height: 1.6;
  color: rgba(226, 232, 240, 0.82);
`

const ProgressTrack = styled.div`
  margin-top: 18px;
  width: 100%;
  height: 10px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.2);
`

const ProgressFill = styled.div<{ $progress: number }>`
  width: ${props => `${props.$progress * 100}%`};
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #64748b 0%, #94a3b8 100%);
  transition: width 220ms ease;
`

const ProgressMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;
  font-size: 12px;
  color: rgba(226, 232, 240, 0.72);
`

type Props = {
  children?: React.ReactNode
  title?: string
  description?: React.ReactNode
  progress?: number
}

export const LoadingScreen = ({ children, title = 'Loading', description, progress }: Props) => {
  const clampedProgress = typeof progress === 'number' ? Math.min(1, Math.max(0, progress)) : undefined
  const content = description ?? children
  const progressLabel = typeof clampedProgress === 'number' ? `${Math.round(clampedProgress * 100)}%` : null

  return (
    <Wrapper>
      <Panel role="status" aria-live="polite">
        <Eyebrow>Workspace</Eyebrow>
        <Title>{title}</Title>
        {content ? <Description>{content}</Description> : null}
        {typeof clampedProgress === 'number' ? (
          <React.Fragment>
            <ProgressTrack>
              <ProgressFill $progress={clampedProgress} />
            </ProgressTrack>
            <ProgressMeta>
              <span>Preparing scene</span>
              <span>{progressLabel}</span>
            </ProgressMeta>
          </React.Fragment>
        ) : null}
      </Panel>
    </Wrapper>
  )
}
